import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Form, InputNumber, Tooltip, message } from 'antd';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, {
    Navigation,
} from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import HeadlineTipImage1 from 'assets/uniprofile/headlinetip1.jpg';
import HeadlineTipImage2 from 'assets/uniprofile/headlinetip2.jpg';
import HeadlinesEditor from 'components/AIHeadlinesEditor';
import AiTextEditor from 'components/AI/AiTextEditor';
import PhotoEditorModal from 'components/PhotoEditorModal';
import { EAIEditorType } from 'types/ai';
import ProfileTip from 'assets/practiceFront/PF_profile_tip.png';
import { QuestionCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { ProfessionalProfileInfo } from 'types/newUniprofile';
import DefaultProfileAvatar from 'assets/common/defaultProviderAvatar.svg';
import { editProfessionalProfile } from 'api/newUniprofile';

SwiperCore.use([Navigation]);

interface Props {
    initValue?: ProfessionalProfileInfo;
    isEditing?: boolean;
    refetch: () => void;
}

const ProfessionalProfile = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, refetch } = props;
    const [form] = Form.useForm<ProfessionalProfileInfo>();

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue(initValue);
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const params = await form.validateFields();
            const result = await editProfessionalProfile({
                ...initValue,
                ...params,
            });
            if (result.error) {
                message.error(result.error);
            } else {
                refetch();
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => {
            return form.validateFields();
        },
    }));

    const renderEditing = () => {
        return (
            <div className={s.wrap}>
                <Form
                    form={form}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Form.Item
                        noStyle
                    >
                        <Form.Item
                            label="Years of experience in healthcare"
                            name="yearExp"
                            rules={[{ required: true, message: 'Years of experience is required' }]}
                        >
                            <InputNumber min={0} controls={false} style={{ width: '320px' }} />
                        </Form.Item>
                        <Form.Item
                            style={{ width: '736px' }}
                            label={
                                <div className={s.formLabel}>Headlines
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{ padding: '24px' }}
                                        color="#FFFFFF"
                                        title={
                                            <>
                                                <div className={s.tipText}>Provide a punchy headline about yourself to stand out. Your headline will appear on all channels' profiles, including your Practice Front and Klarity profile page.</div>
                                                <Swiper navigation>
                                                    <SwiperSlide key={0} className={s.leftWrap}>
                                                        <div style={{ width: '344px', margin: '0 auto' }}>
                                                            <img src={HeadlineTipImage1} />
                                                        </div>
                                                    </SwiperSlide>
                                                    <SwiperSlide key={1} className={s.leftWrap}>
                                                        <div style={{ width: '344px', margin: '0 auto' }}>
                                                            <img src={HeadlineTipImage2} />
                                                        </div>
                                                    </SwiperSlide>
                                                </Swiper>
                                            </>
                                        }
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name="headLine"
                            rules={[
                                { required: true, message: 'Headline is a required field' },
                                { max: 220, message: 'Exceeded maximum character length of 220' },
                            ]}
                        >
                            <HeadlinesEditor options={initValue?.headLineList} conversationId={initValue?.headLineConversationId} />
                        </Form.Item>
                        <Form.Item
                            style={{ width: '736px' }}
                            label={
                                <div className={s.formLabel}>About me
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        color="#FFFFFF"
                                        title={<div className={s.tipText2}>What would you like to share with your patients about yourself?</div>}
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name="intro"
                            rules={[
                                { required: true, message: 'Headline is a required field' },
                            ]}
                        >
                            <AiTextEditor
                                maxLength={1000}
                                type={EAIEditorType.ABOUTME}
                                placeholder="Start writing, then use Ava to polish, adjust tone, or refine your style."
                                style={{ height: '85px' }}
                                conversionId={initValue?.aboutMeConversationId}
                            />
                        </Form.Item>
                        <Form.Item
                            style={{ width: '300px' }}
                            label={
                                <div className={s.formLabel}>Profile photo
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{ padding: '12px' }}
                                        color="#FFFFFF"
                                        title={
                                            <div className={s.tipTextWrap}>
                                                <div>Upload a professional photo of yourself with a preferred background. A top-notch profile photo is likely to generate 3x more clicks. You can crop and rotate the image as needed.</div>
                                                <div>Photos that include a background typically achieve the best possible appearance and visibility across various channels. <img src={ProfileTip} /></div>
                                            </div>}
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name="photo"
                            rules={[{ required: true, message: 'Profile photo is a required field' }]}
                        >
                            <PhotoEditorModal
                                borderRadius
                                title="Edit photo"
                                providerId={initValue?.providerId!}
                            />
                        </Form.Item>
                    </Form.Item>
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        return (
            (
                <div className={s.subModule}>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={`${s.label} ${s.required}`}>Years of experience in healthcare</div>
                            <div className={s.value}>{initValue?.yearExp || '-'}</div>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={`${s.label} ${s.required}`}>Headline</div>
                            <div className={s.value}>{initValue?.headLine || '-'}</div>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={`${s.label} ${s.required}`}>About me</div>
                            <div
                                className={s.value}
                                dangerouslySetInnerHTML={{
                                    __html: initValue?.intro?.replace(/\n/g, '<br />') || '-',
                                }}
                            />
                        </div>
                    </div>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={`${s.label} ${s.required}`}>Profile photo</div>
                            <img className={s.profileAvatar} src={initValue?.photo || DefaultProfileAvatar} />
                        </div>
                    </div>
                </div>
            )
        );
    };

    return (
        <div>
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default ProfessionalProfile;
